import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { MessageSquare, Loader2, CheckCircle2, Send, ShieldCheck, Plus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    teams_webhook_url: "",
    teams_notifications_enabled: false,
    staff_remote_access: true,
    staff_allowed_ips: [],
  });
  const [newIp, setNewIp] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true });
      setForm({
        teams_webhook_url: res.data.teams_webhook_url || "",
        teams_notifications_enabled: !!res.data.teams_notifications_enabled,
        staff_remote_access: res.data.staff_remote_access !== false,
        staff_allowed_ips: res.data.staff_allowed_ips || [],
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    if (form.staff_allowed_ips.includes(ip)) {
      toast.info("Already in the list");
      return;
    }
    setForm((p) => ({ ...p, staff_allowed_ips: [...p.staff_allowed_ips, ip] }));
    setNewIp("");
  };

  const removeIp = (ip) => {
    setForm((p) => ({ ...p, staff_allowed_ips: p.staff_allowed_ips.filter((x) => x !== ip) }));
  };

  const handleTest = async () => {
    if (!form.teams_webhook_url.trim()) {
      toast.error("Enter a Teams webhook URL first");
      return;
    }
    // Save first so the backend has the latest URL
    setTesting(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      await axios.post(`${API}/settings/test-teams-webhook`, {}, { withCredentials: true });
      toast.success("Test message sent — check Teams!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test message");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Settings</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Configure integrations and notifications for your workspace.
          </p>
        </div>

        {/* Microsoft Teams Integration */}
        <section className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5"
          data-testid="teams-settings-section">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#464EB8]/10 flex items-center justify-center rounded">
              <MessageSquare className="w-5 h-5 text-[#464EB8]" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Microsoft Teams</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Post a message to a Teams channel whenever you publish a roster.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Incoming Webhook URL
              </Label>
              <Input
                type="url"
                placeholder="https://yourorg.webhook.office.com/webhookb2/..."
                value={form.teams_webhook_url}
                onChange={(e) => setForm((p) => ({ ...p, teams_webhook_url: e.target.value }))}
                className="mt-1 font-mono text-xs"
                data-testid="teams-webhook-input"
              />
              <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1.5">
                In Teams: channel → Workflows → "Post to a channel when a webhook request is received" → copy the generated URL.
              </p>
            </div>

            <div className="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded border border-zinc-200 dark:border-zinc-800">
              <div>
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Enable notifications</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Send a Teams card each time a roster is published.
                </p>
              </div>
              <Switch
                checked={form.teams_notifications_enabled}
                onCheckedChange={(v) => setForm((p) => ({ ...p, teams_notifications_enabled: v }))}
                data-testid="teams-enabled-switch"
              />
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Save changes
              </Button>
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={testing || !form.teams_webhook_url.trim()}
                data-testid="test-teams-btn"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send test message
              </Button>
            </div>
          </div>
        </section>

        {/* Staff Portal Access Controls */}
        <section className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 space-y-5"
          data-testid="staff-access-section">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center rounded">
              <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Staff Portal Access</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Restrict which networks your staff can use to access their roster.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded border border-zinc-200 dark:border-zinc-800">
              <div className="pr-3">
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Allow staff to access from anywhere
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  When enabled, staff can log in to the staff portal from any network. Disable for stricter security.
                </p>
              </div>
              <Switch
                checked={form.staff_remote_access}
                onCheckedChange={(v) => setForm((p) => ({ ...p, staff_remote_access: v }))}
                data-testid="staff-remote-access-switch"
              />
            </div>

            {!form.staff_remote_access && (
              <div className="border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-4 rounded space-y-3" data-testid="staff-allowed-ips-block">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-amber-800 dark:text-amber-300">
                    Allowed networks
                  </Label>
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                    Add the office IP addresses or CIDR ranges that staff can connect from. Anyone outside this list will be blocked.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="192.168.1.0/24 or 203.0.113.42"
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIp(); } }}
                    className="font-mono text-xs"
                    data-testid="staff-ip-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addIp}
                    data-testid="staff-ip-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {form.staff_allowed_ips.length === 0 ? (
                  <p className="text-xs text-amber-700 dark:text-amber-400 italic">
                    No IPs added yet — staff will be blocked entirely until you add at least one.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {form.staff_allowed_ips.map((ip) => (
                      <span
                        key={ip}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono bg-white dark:bg-zinc-900 border border-amber-300 dark:border-amber-800 rounded"
                        data-testid={`staff-ip-chip-${ip}`}
                      >
                        {ip}
                        <button
                          type="button"
                          onClick={() => removeIp(ip)}
                          className="text-zinc-400 hover:text-red-600"
                          aria-label={`Remove ${ip}`}
                          data-testid={`staff-ip-remove-${ip}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="save-staff-access-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Save access controls
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Settings;
